import React, { useState, useEffect } from 'react';
import { AdminService } from '../services/adminService';
import { User } from '../services/firebaseAuth';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Shield,
  Eye,
  UserCheck,
  UserX,
  Crown,
  Settings,
  TrendingUp,
  User as UserIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getAvailableRoles, getRoleDisplayName, getRoleDescription } from '../utils/permissions';

interface AdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected' | 'suspended' | 'all'>('approved');
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [approvedUsers, setApprovedUsers] = useState<User[]>([]);
  const [rejectedUsers, setRejectedUsers] = useState<User[]>([]);
  const [suspendedUsers, setSuspendedUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    suspended: 0
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<{ [key: string]: boolean }>({});

  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pending, approved, rejected, suspended, userStats, all] = await Promise.all([
        AdminService.getPendingUsers(),
        AdminService.getApprovedUsers(),
        AdminService.getRejectedUsers(),
        AdminService.getSuspendedUsers(),
        AdminService.getUserStats(),
        AdminService.getAllUsers()
      ]);
      
      setPendingUsers(pending);
      setApprovedUsers(approved);
      setRejectedUsers(rejected);
      setSuspendedUsers(suspended);
      setAllUsers(all);
      setStats(userStats);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: string) => {
    if (!currentUser) return;
    
    try {
      await AdminService.approveUser(userId, currentUser.uid);
      toast.success('User approved successfully');
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to approve user');
    }
  };

  const handleReject = async (userId: string) => {
    if (!currentUser || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    try {
      await AdminService.rejectUser(userId, currentUser.uid, rejectionReason);
      toast.success('User rejected successfully');
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedUser(null);
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to reject user');
    }
  };

  const handleSuspend = async (userId: string) => {
    if (!currentUser || !rejectionReason.trim()) {
      toast.error('Please provide a suspension reason');
      return;
    }
    
    try {
      await AdminService.suspendUser(userId, currentUser.uid, rejectionReason);
      toast.success('User suspended successfully');
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedUser(null);
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to suspend user');
    }
  };

  const handleReapprove = async (userId: string) => {
    if (!currentUser) return;
    
    try {
      await AdminService.reapproveUser(userId, currentUser.uid);
      toast.success('User reapproved successfully');
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to reapprove user');
    }
  };

  const handleUnsuspend = async (userId: string) => {
    if (!currentUser) return;
    
    try {
      await AdminService.unsuspendUser(userId, currentUser.uid);
      toast.success('User unsuspended successfully');
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to unsuspend user');
    }
  };

  const handleRoleChange = async (userId: string, newRole: User['role']) => {
    if (!currentUser) return;
    
    try {
      await AdminService.changeUserRole(userId, newRole, currentUser.uid, currentUser.role);
      toast.success('User role updated successfully');
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user role');
    }
  };

  const getRoleIcon = (role: User['role']) => {
    switch (role) {
      case 'owner': return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'admin': return <Shield className="h-4 w-4 text-blue-500" />;
      case 'trader': return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'user': return <UserIcon className="h-4 w-4 text-gray-500" />;
      default: return <UserIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusIcon = (status: User['status']) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'approved': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'suspended': return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  // Helper function to handle both Firestore Timestamps and regular Date objects
  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    
    // Check if it's a Firestore Timestamp
    if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
      return timestamp.toDate().toLocaleString();
    }
    
    // Check if it's a regular Date object
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }
    
    // Try to create a Date from the timestamp
    try {
      return new Date(timestamp).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Helper function to safely validate user data
  const validateUserData = (user: User): boolean => {
    return !!(user && 
           user.uid && 
           user.email && 
           user.status && 
           user.role);
  };

  // Helper function to safely access nested properties
  const safeGet = (obj: any, path: string, defaultValue: any = 'N/A') => {
    try {
      return path.split('.').reduce((current, key) => current?.[key], obj) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 text-[#2AB7A6]" />
            <div>
              <h2 className="text-lg font-semibold text-white">User Management</h2>
              <p className="text-xs text-gray-400">
                Logged in as: {getRoleDisplayName(currentUser?.role || 'user')} - {getRoleDescription(currentUser?.role || 'user')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        {/* Stats Bar */}
        <div className="bg-gray-700 px-6 py-3 border-b border-gray-600">
          <div className="flex space-x-6 text-xs">
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-gray-400" />
              <span className="text-gray-300">Total: {stats.total}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-yellow-500" />
              <span className="text-yellow-400">Pending: {stats.pending}</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-green-400">Approved: {stats.approved}</span>
            </div>
            <div className="flex items-center space-x-2">
              <XCircle className="h-4 w-4 text-red-500" />
              <span className="text-red-400">Rejected: {stats.rejected}</span>
            </div>
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              <span className="text-orange-400">Suspended: {stats.suspended}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-gray-700 px-6 border-b border-gray-600">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors text-xs ${
                activeTab === 'pending'
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4" />
                <span>Pending Approval ({stats.pending})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors text-xs ${
                activeTab === 'approved'
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4" />
                <span>Approved Users ({stats.approved})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors text-xs ${
                activeTab === 'rejected'
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <XCircle className="h-4 w-4" />
                <span>Rejected Users ({stats.rejected})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('suspended')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors text-xs ${
                activeTab === 'suspended'
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4" />
                <span>Suspended Users ({stats.suspended})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors text-xs ${
                activeTab === 'all'
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4" />
                <span>All Users Detailed Info ({stats.total})</span>
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2AB7A6]"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {activeTab === 'pending' && (
                pendingUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-xs">No users pending approval</p>
                  </div>
                ) : (
                  pendingUsers
                    .filter(validateUserData)
                    .map((user) => (
                      <div key={user.uid} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                        <div className="flex items-start space-x-4">
                          {/* Left Column - Avatar and User Info */}
                          <div className="flex items-center space-x-4 flex-1">
                            <div className="h-10 w-10 bg-[#2AB7A6] rounded-full flex items-center justify-center">
                              <span className="text-white font-semibold text-xs">
                                {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                              </span>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-white font-medium text-xs">{user.displayName}</h3>
                              <p className="text-gray-400 text-xs">{user.email}</p>
                              <div className="flex items-center space-x-2 mt-1">
                                {getRoleIcon(user.role)}
                                <span className="text-xs text-gray-500 capitalize">{user.role}</span>
                                {getStatusIcon(user.status)}
                                <span className="text-xs text-yellow-500">Pending</span>
                              </div>
                              
                              {/* Login Attempts */}
                              {user.loginAttempts && user.loginAttempts.count > 0 && (
                                <div className="mt-2 p-2 bg-gray-600/30 rounded border border-gray-500">
                                  <h5 className="text-xs font-medium text-orange-400 mb-1">Login Attempts</h5>
                                  <div className="text-xs">
                                    <p className="text-gray-300">Failed attempts: {user.loginAttempts.count}</p>
                                    {user.loginAttempts.blockedUntil && (
                                      <p className="text-red-400">
                                        Blocked until: {new Date(user.loginAttempts.blockedUntil).toLocaleString()}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Middle Column - KYC Information */}
                          {user.kycDocuments && (
                            <div className="flex-[2] p-3 bg-gray-600/50 rounded border border-gray-500">
                              <h4 className="text-xs font-medium text-blue-400 mb-2">KYC Documents Submitted</h4>
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                                <div>
                                  <span className="text-gray-400">ID/Passport:</span>
                                  <p className="text-white">
                                    {user.kycDocuments.nationalIdPassport?.type === 'national_id' ? 'National ID' : 
                                     user.kycDocuments.nationalIdPassport?.type === 'passport' ? 'Passport' :
                                     user.kycDocuments.nationalIdPassport?.type === 'driving_license' ? 'Driving License' : 'N/A'}
                                  </p>
                                  <p className="text-gray-300">{user.kycDocuments.nationalIdPassport?.number || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">License/Card:</span>
                                  <p className="text-white">
                                    {user.kycDocuments.licenseGovernmentCard?.type === 'license' ? 'Driver License' : 
                                     user.kycDocuments.licenseGovernmentCard?.type === 'government_card' ? 'Gov Card' : 'N/A'}
                                  </p>
                                  <p className="text-gray-300">{user.kycDocuments.licenseGovernmentCard?.number || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Tax ID:</span>
                                  <p className="text-white">
                                    {user.kycDocuments.taxId?.type === 'pan' ? 'PAN' : 
                                     user.kycDocuments.taxId?.type === 'ssn' ? 'SSN' : 
                                     user.kycDocuments.taxId?.type ? 'Tax ID' : 'N/A'}
                                  </p>
                                  <p className="text-gray-300">{user.kycDocuments.taxId?.number || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">KYC Submitted:</span>
                                  <p className="text-white">
                                    {user.kycSubmittedAt ? formatTimestamp(user.kycSubmittedAt) : 'N/A'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Right Column - Action Buttons */}
                          <div className="flex flex-col items-end space-y-2">
                            <button
                              onClick={() => handleApprove(user.uid)}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors flex items-center space-x-1 w-24 justify-center"
                            >
                              <UserCheck className="h-3 w-3" />
                              <span>Approve</span>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowRejectModal(true);
                              }}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors flex items-center space-x-1 w-24 justify-center"
                            >
                              <UserX className="h-3 w-3" />
                              <span>Reject</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                )
              )}

              {activeTab === 'approved' && (
                approvedUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-xs">No approved users yet</p>
                  </div>
                ) : (
                  approvedUsers
                    .filter(validateUserData)
                    .map((user) => (
                                             <div key={user.uid} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                                                  <div className="flex items-start justify-between">
                            {/* Left Column - User Details */}
                            <div className="flex items-center space-x-4 w-80">
                             <div className="h-10 w-10 bg-[#2AB7A6] rounded-full flex items-center justify-center">
                               <span className="text-white font-semibold text-xs">
                                 {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                               </span>
                             </div>
                             <div className="flex-1">
                               <h3 className="text-white font-medium text-xs">{user.displayName}</h3>
                               <p className="text-gray-400 text-xs">{user.email}</p>
                               <div className="flex items-center space-x-2 mt-1">
                                 {getRoleIcon(user.role)}
                                 <span className="text-xs text-gray-500 capitalize">{user.role}</span>
                                 {getStatusIcon(user.status)}
                                 <span className="text-xs text-green-500">Approved</span>
                               </div>
                               {user.approvedAt && (
                                 <p className="text-xs text-gray-500 mt-1">
                                   Approved: {formatTimestamp(user.approvedAt)}
                                 </p>
                               )}
                             </div>
                           </div>

                                                  {/* Middle Column - User Information */}
                           {user.metadata && (
                             <div className="w-80 p-3 bg-gray-600/50 rounded border border-gray-500">
                               <h4 className="text-xs font-medium text-green-400 mb-2 text-start">User Information</h4>
                               <div className="space-y-2 text-xs">
                                 {/* Basic Info */}
                                 <div>
                                   <span className="text-gray-400">Created: </span>
                                   <span className="text-white">{formatTimestamp(user.createdAt)}</span>
                                 </div>
                                 {user.approvedAt && (
                                   <div>
                                     <span className="text-gray-400">Last Updated: </span>
                                     <span className="text-white">{formatTimestamp(user.approvedAt)}</span>
                                   </div>
                                 )}
                                 
                                 {/* Location & Device Info */}
                                 {user.metadata?.signup && (
                                   <>
                                     {/* Device, IP Address, and Location information removed */}
                                   </>
                                 )}

                                 {/* Last Login Location */}
                                {user.metadata?.lastLogin && (
                                  <div>
                                    <span className="text-gray-400">Last Login Location: </span>
                                    <span className="text-white">
                                      {user.metadata.lastLogin.city && user.metadata.lastLogin.country 
                                        ? `${user.metadata.lastLogin.city}, ${user.metadata.lastLogin.country}`
                                        : user.metadata.lastLogin.town || 'N/A'
                                      }
                                    </span>
                                  </div>
                                )}
                             </div>
                           </div>
                         )}

                                                                                                   {/* Right Column - Action Buttons */}
                          <div className="flex flex-col items-end space-y-2">
                           {/* Custom Role Dropdown */}
                           <div className="relative">
                             <button
                               onClick={() => setOpenDropdowns(prev => ({ ...prev, [`role-${user.uid}`]: !prev[`role-${user.uid}`] }))}
                               className="px-3 py-1 bg-gray-600 text-white text-xs rounded border border-gray-500 focus:outline-none focus:border-[#2AB7A6] w-32 flex items-center justify-between"
                             >
                               <span>{getRoleDisplayName(user.role)}</span>
                               <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                               </svg>
                             </button>
                             
                             {openDropdowns[`role-${user.uid}`] && (
                               <div className="absolute top-full left-0 mt-1 w-32 bg-gray-700 border border-gray-600 rounded-md shadow-lg z-10">
                                 {getAvailableRoles(currentUser?.role || 'user').map(role => (
                                   <button
                                     key={role}
                                     onClick={() => {
                                       handleRoleChange(user.uid, role);
                                       setOpenDropdowns(prev => ({ ...prev, [`role-${user.uid}`]: false }));
                                     }}
                                     className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                                       user.role === role 
                                         ? 'bg-[#2AB7A6] text-white' 
                                         : 'text-gray-300 hover:bg-gray-600'
                                     }`}
                                   >
                                     {getRoleDisplayName(role)}
                                   </button>
                                 ))}
                               </div>
                             )}
                           </div>
                           <button
                             onClick={() => {
                               setSelectedUser(user);
                               setShowRejectModal(true);
                             }}
                             className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded transition-colors flex items-center space-x-1 w-32 justify-center"
                           >
                             <AlertTriangle className="h-3 w-3" />
                             <span>Suspend</span>
                           </button>
                         </div>
                      </div>
                    </div>
                  ))
                )
              )}

              {activeTab === 'rejected' && (
                rejectedUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <XCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-xs">No rejected users</p>
                  </div>
                ) : (
                  rejectedUsers.map((user) => (
                    <div key={user.uid} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                      <div className="flex items-start space-x-4">
                        {/* Left Column - User Details */}
                        <div className="flex items-center space-x-4 flex-1">
                          <div className="h-10 w-10 bg-red-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-xs">
                              {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white font-medium text-xs">{user.displayName}</h3>
                            <p className="text-gray-400 text-xs">{user.email}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              {getRoleIcon(user.role)}
                              <span className="text-xs text-gray-500 capitalize">{user.role}</span>
                              {getStatusIcon(user.status)}
                              <span className="text-xs text-red-500">Rejected</span>
                            </div>
                            
                            {/* Rejection Reason */}
                            {user.rejectionReason && (
                              <div className="mt-3 p-3 bg-red-600/20 rounded border border-red-500/50">
                                <h4 className="text-xs font-medium text-red-400 mb-2">Rejection Reason</h4>
                                <p className="text-red-300 text-xs">{user.rejectionReason}</p>
                                {user.approvedAt && (
                                  <p className="text-xs text-red-400 mt-2">
                                    Rejected on: {new Date(user.approvedAt).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* User Information */}
                            {user.metadata && (
                              <div className="mt-3 p-3 bg-gray-600/50 rounded border border-gray-500">
                                <h4 className="text-xs font-medium text-green-400 mb-2">User Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                  {/* Basic Info */}
                                  <div>
                                    <span className="text-gray-400">Created:</span>
                                    <p className="text-white">{user.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A'}</p>
                                    {user.approvedAt && (
                                      <>
                                        <span className="text-gray-400">Last Updated:</span>
                                        <p className="text-white">{new Date(user.approvedAt).toLocaleString()}</p>
                                      </>
                                    )}
                                  </div>
                                  
                                  {/* Location & Device Info */}
                                  <div>
                                    {user.metadata.signup && (
                                      <>
                                        <span className="text-gray-400">Device:</span>
                                        <p className="text-white">{user.metadata.signup.browser} on {user.metadata.signup.operatingSystem}</p>
                                        {user.metadata.signup.ipAddress && (
                                          <p className="text-gray-300">IP: {user.metadata.signup.ipAddress}</p>
                                        )}
                                        {user.metadata.signup.country && (
                                          <p className="text-gray-300">{user.metadata.signup.city}, {user.metadata.signup.country}</p>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleReapprove(user.uid)}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors flex items-center space-x-1"
                          >
                            <CheckCircle className="h-3 w-3" />
                            <span>Reapprove</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )
              )}

              {activeTab === 'suspended' && (
                suspendedUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-xs">No suspended users</p>
                  </div>
                ) : (
                  suspendedUsers.map((user) => (
                    <div key={user.uid} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="h-10 w-10 bg-orange-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-xs">
                              {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white font-medium text-xs">{user.displayName}</h3>
                            <p className="text-gray-400 text-xs">{user.email}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              {getRoleIcon(user.role)}
                              <span className="text-xs text-gray-500 capitalize">{user.role}</span>
                              {getStatusIcon(user.status)}
                              <span className="text-xs text-orange-500">Suspended</span>
                            </div>
                            
                            {/* Suspension Reason */}
                            {user.rejectionReason && (
                              <div className="mt-3 p-3 bg-orange-600/20 rounded border border-orange-500/50">
                                <h4 className="text-xs font-medium text-orange-400 mb-2">Suspension Reason</h4>
                                <p className="text-orange-300 text-xs">{user.rejectionReason}</p>
                                {user.approvedAt && (
                                  <p className="text-xs text-orange-400 mt-2">
                                    Suspended on: {new Date(user.approvedAt).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* User Information */}
                            {user.metadata && (
                              <div className="mt-3 p-3 bg-gray-600/50 rounded border border-gray-500">
                                <h4 className="text-xs font-medium text-green-400 mb-2">User Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                  {/* Basic Info */}
                                  <div>
                                    <span className="text-gray-400">Created:</span>
                                    <p className="text-white">{user.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A'}</p>
                                    {user.approvedAt && (
                                      <>
                                        <span className="text-gray-400">Last Updated:</span>
                                        <p className="text-white">{new Date(user.approvedAt).toLocaleString()}</p>
                                      </>
                                    )}
                                  </div>
                                  
                                  {/* Location & Device Info */}
                                  <div>
                                    {user.metadata.signup && (
                                      <>
                                        <span className="text-gray-400">Device:</span>
                                        <p className="text-white">{user.metadata.signup.browser} on {user.metadata.signup.operatingSystem}</p>
                                        {user.metadata.signup.ipAddress && (
                                          <p className="text-gray-300">IP: {user.metadata.signup.ipAddress}</p>
                                        )}
                                        {user.metadata.signup.country && (
                                          <p className="text-gray-300">{user.metadata.signup.city}, {user.metadata.signup.country}</p>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Login Attempts */}
                            {user.loginAttempts && user.loginAttempts.count > 0 && (
                              <div className="mt-3 p-3 bg-gray-600/50 rounded border border-gray-500">
                                <h4 className="text-xs font-medium text-orange-400 mb-2">Login Attempts</h4>
                                <div className="text-xs">
                                  <p className="text-gray-300">Failed attempts: {user.loginAttempts.count}</p>
                                  {user.loginAttempts.blockedUntil && (
                                    <p className="text-red-400">
                                      Blocked until: {new Date(user.loginAttempts.blockedUntil).toLocaleString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleUnsuspend(user.uid)}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors flex items-center space-x-1"
                          >
                            <CheckCircle className="h-3 w-3" />
                            <span>Unsuspend</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )
              )}

              {activeTab === 'all' && (
                allUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-xs">No users found</p>
                  </div>
                ) : (
                  allUsers.map((user) => (
                    <div key={user.uid} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                                             <div className="grid grid-cols-4 gap-4">
                         {/* Column 1: Basic User Information */}
                         <div className="w-full space-y-3">
                           <h4 className="text-xs font-medium text-blue-400 border-b border-gray-600 pb-2">Basic Information</h4>
                           <div className="space-y-2 text-xs">
                            <div>
                              <span className="text-gray-400">User ID:</span>
                              <p className="text-white font-mono">{user.uid}</p>
                            </div>
                            <div>
                              <span className="text-gray-400">Display Name:</span>
                              <p className="text-white">{user.displayName || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-gray-400">Email:</span>
                              <p className="text-white">{user.email || 'N/A'}</p>
                            </div>
                            {user.photoURL && (
                              <div>
                                <span className="text-gray-400">Photo URL:</span>
                                <p className="text-white">{user.photoURL}</p>
                              </div>
                            )}
                            <div>
                              <span className="text-gray-400">Role:</span>
                              <div className="flex items-center space-x-2">
                                {getRoleIcon(user.role)}
                                <span className="text-white capitalize">{user.role}</span>
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-400">Status:</span>
                              <div className="flex items-center space-x-2">
                                {getStatusIcon(user.status)}
                                <span className={`text-white capitalize ${
                                  user.status === 'approved' ? 'text-green-400' :
                                  user.status === 'rejected' ? 'text-red-400' :
                                  user.status === 'suspended' ? 'text-orange-400' :
                                  'text-yellow-400'
                                }`}>{user.status}</span>
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-400">Created At:</span>
                              <p className="text-white">{formatTimestamp(user.createdAt)}</p>
                            </div>
                                                         {user.lastLoginAt && (
                               <div>
                                 <span className="text-gray-400">Last Login:</span>
                                 <p className="text-white">{formatTimestamp(user.lastLoginAt)}</p>
                               </div>
                             )}
                             {user.approvedAt && (
                               <div>
                                 <span className="text-gray-400">Approved At:</span>
                                 <p className="text-white">{formatTimestamp(user.approvedAt)}</p>
                               </div>
                             )}
                             {user.approvedBy && (
                               <div>
                                 <span className="text-gray-400">Approved By:</span>
                                 <p className="text-white">{user.approvedBy}</p>
                               </div>
                             )}
                             {user.photoURL && (
                               <div>
                                 <span className="text-gray-400">Photo URL:</span>
                                 <p className="text-white">{user.photoURL}</p>
                               </div>
                             )}
                             {user.metadata?.lastLogin && (
                               <div>
                                 <span className="text-gray-400">Last Login Location:</span>
                                 <p className="text-white">
                                   {user.metadata.lastLogin.city && user.metadata.lastLogin.country 
                                     ? `${user.metadata.lastLogin.city}, ${user.metadata.lastLogin.country}`
                                     : user.metadata.lastLogin.town || 'N/A'
                                   }
                                 </p>
                               </div>
                             )}
                          </div>
                        </div>

                                                 {/* Column 2: Location & Device Information */}
                         <div className="w-full space-y-3">
                           <h4 className="text-xs font-medium text-green-400 border-b border-gray-600 pb-2">Location & Device</h4>
                           <div className="space-y-2 text-xs">
                            {user.metadata?.signup ? (
                              <>
                                <div>
                                  <span className="text-gray-400">IP Address:</span>
                                  <p className="text-white">{user.metadata.signup.ipAddress || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Location:</span>
                                  <p className="text-white">
                                    {user.metadata.signup.city && user.metadata.signup.country 
                                      ? `${user.metadata.signup.city}, ${user.metadata.signup.country}`
                                      : user.metadata.signup.town || 'N/A'
                                    }
                                  </p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Country:</span>
                                  <p className="text-white">{user.metadata.signup.country || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">City/Town:</span>
                                  <p className="text-white">{user.metadata.signup.city || user.metadata.signup.town || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Device:</span>
                                  <p className="text-white">{user.metadata.signup.browser} on {user.metadata.signup.operatingSystem}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">User Agent:</span>
                                  <p className="text-white text-xs break-all">{user.metadata.signup.userAgent}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Screen Resolution:</span>
                                  <p className="text-white">{user.metadata.signup.screenResolution}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Timezone:</span>
                                  <p className="text-white">{user.metadata.signup.timezone}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Language:</span>
                                  <p className="text-white">{user.metadata.signup.language}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Signup Time:</span>
                                  <p className="text-white">{formatTimestamp(user.metadata.signup.timestamp)}</p>
                                </div>
                              </>
                            ) : (
                              <p className="text-gray-500">No signup metadata available</p>
                            )}

                            {/* Signup Metadata Section */}
                            {/* {user.metadata?.signup && (
                              <>
                                <div className="pt-2 border-t border-gray-600">
                                  <h5 className="text-xs font-medium text-blue-400 mb-2">Signup Information</h5>
                                  <div className="space-y-2">
                                    <div>
                                      <span className="text-gray-400">Signup IP Address:</span>
                                      <p className="text-white">{user.metadata.signup.ipAddress || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Signup Location:</span>
                                      <p className="text-white">
                                        {user.metadata.signup.city && user.metadata.signup.country 
                                          ? `${user.metadata.signup.city}, ${user.metadata.signup.country}`
                                          : user.metadata.signup.town || 'N/A'
                                        }
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Signup Device:</span>
                                      <p className="text-white">{user.metadata.signup.browser} on {user.metadata.signup.operatingSystem}</p>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Signup User Agent:</span>
                                      <p className="text-white text-xs break-all">{user.metadata.signup.userAgent || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Signup Screen Resolution:</span>
                                      <p className="text-white">{user.metadata.signup.screenResolution || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Signup Timezone:</span>
                                      <p className="text-white">{user.metadata.signup.timezone || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Signup Language:</span>
                                      <p className="text-white">{user.metadata.signup.language || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Signup Time:</span>
                                      <p className="text-white">{formatTimestamp(user.metadata.signup.timestamp)}</p>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )} */}
                            
                                                         {user.metadata?.lastLogin && (
                               <>
                                 <div className="pt-2 border-t border-gray-600">
                                   <span className="text-gray-400">Last Login IP:</span>
                                   <p className="text-white">{user.metadata.lastLogin.ipAddress || 'N/A'}</p>
                                 </div>
                                 <div>
                                   <span className="text-gray-400">Last Login Location:</span>
                                   <p className="text-white">
                                     {user.metadata.lastLogin.city && user.metadata.lastLogin.country 
                                       ? `${user.metadata.lastLogin.city}, ${user.metadata.lastLogin.country}`
                                       : user.metadata.lastLogin.town || 'N/A'
                                     }
                                   </p>
                                 </div>
                                 <div>
                                   <span className="text-gray-400">Last Login Browser:</span>
                                   <p className="text-white">{user.metadata.lastLogin.browser || 'N/A'}</p>
                                 </div>
                                 <div>
                                   <span className="text-gray-400">Last Login OS:</span>
                                   <p className="text-white">{user.metadata.lastLogin.operatingSystem || 'N/A'}</p>
                                 </div>
                                 
                                 <div>
                                   <span className="text-gray-400">Last Login Timezone:</span>
                                   <p className="text-white">{user.metadata.lastLogin.timezone || 'N/A'}</p>
                                 </div>
                                 <div>
                                   <span className="text-gray-400">Last Login Language:</span>
                                   <p className="text-white">{user.metadata.lastLogin.language || 'N/A'}</p>
                                 </div>
                               </>
                             )}
                          </div>
                        </div>

                                                 {/* Column 3: KYC & Security Information */}
                         <div className="w-full space-y-3">
                           <h4 className="text-xs font-medium text-purple-400 border-b border-gray-600 pb-2">KYC & Security</h4>
                           <div className="space-y-2 text-xs">
                            {user.kycDocuments ? (
                              <>
                                <div>
                                  <span className="text-gray-400">KYC Status:</span>
                                  <p className="text-green-400">Documents Submitted</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">ID/Passport Type:</span>
                                  <p className="text-white">
                                    {user.kycDocuments.nationalIdPassport?.type === 'national_id' ? 'National ID' : 
                                     user.kycDocuments.nationalIdPassport?.type === 'passport' ? 'Passport' :
                                     user.kycDocuments.nationalIdPassport?.type === 'driving_license' ? 'Driving License' : 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-gray-400">ID/Passport Number:</span>
                                  <p className="text-white font-mono">{user.kycDocuments.nationalIdPassport?.number || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">License/Card Type:</span>
                                  <p className="text-white">
                                    {user.kycDocuments.licenseGovernmentCard?.type === 'license' ? 'Driver License' : 
                                     user.kycDocuments.licenseGovernmentCard?.type === 'government_card' ? 'Government Card' : 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-gray-400">License/Card Number:</span>
                                  <p className="text-white font-mono">{user.kycDocuments.licenseGovernmentCard?.number || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Tax ID Type:</span>
                                  <p className="text-white">
                                    {user.kycDocuments.taxId?.type === 'pan' ? 'PAN' : 
                                     user.kycDocuments.taxId?.type === 'ssn' ? 'SSN' : 
                                     user.kycDocuments.taxId?.type === 'tax_id' ? 'Tax ID' : 
                                     user.kycDocuments.taxId?.type ? 'Other' : 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Tax ID Number:</span>
                                  <p className="text-white font-mono">{user.kycDocuments.taxId?.number || 'N/A'}</p>
                                </div>
                                {user.kycSubmittedAt && (
                                  <div>
                                    <span className="text-gray-400">KYC Submitted:</span>
                                    <p className="text-white">{formatTimestamp(user.kycSubmittedAt)}</p>
                                  </div>
                                )}
                              </>
                            ) : (
                              <p className="text-gray-500">No KYC documents submitted</p>
                            )}

                                                         {user.loginAttempts && (
                               <>
                                 <div className="pt-2 border-t border-gray-600">
                                   <span className="text-gray-400">Login Attempts Count:</span>
                                   <p className={user.loginAttempts.count > 0 ? 'text-red-400' : 'text-green-400'}>{user.loginAttempts.count}</p>
                                 </div>
                                 {user.loginAttempts.lastAttempt && (
                                   <div>
                                     <span className="text-gray-400">Last Attempt:</span>
                                     <p className="text-white">{formatTimestamp(user.loginAttempts.lastAttempt)}</p>
                                   </div>
                                 )}
                                 {user.loginAttempts.blockedUntil && (
                                   <div>
                                     <span className="text-gray-400">Blocked Until:</span>
                                     <p className="text-red-400">{formatTimestamp(user.loginAttempts.blockedUntil)}</p>
                                   </div>
                                 )}
                               </>
                             )}

                             {user.metadata?.lastLogin && (
                               <>
                                 <div className="pt-2 border-t border-gray-600">
                                   <span className="text-gray-400">Last Login Time:</span>
                                   <p className="text-white">{formatTimestamp(user.metadata.lastLogin.timestamp)}</p>
                                 </div>
                                 <div>
                                   <span className="text-gray-400">Last Login User Agent:</span>
                                   <p className="text-white text-xs break-all">{user.metadata.lastLogin.userAgent || 'N/A'}</p>
                                 </div>
                               </>
                             )}

                            {user.rejectionReason && (
                              <>
                                <div className="pt-2 border-t border-gray-600">
                                  <span className="text-gray-400">Reason:</span>
                                  <p className={`text-xs ${
                                    user.status === 'rejected' ? 'text-red-300' : 'text-orange-300'
                                  }`}>{user.rejectionReason}</p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Column 4: Signup Information */}
                        <div className="w-full space-y-3">
                          <h4 className="text-xs font-medium text-orange-400 border-b border-gray-600 pb-2">Signup Information</h4>
                          <div className="space-y-2 text-xs">
                            {user.metadata?.signup ? (
                              <>
                                <div>
                                  <span className="text-gray-400">Signup IP Address:</span>
                                  <p className="text-white">{user.metadata.signup.ipAddress || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Signup Location:</span>
                                  <p className="text-white">
                                    {user.metadata.signup.city && user.metadata.signup.country 
                                      ? `${user.metadata.signup.city}, ${user.metadata.signup.country}`
                                      : user.metadata.signup.town || 'N/A'
                                    }
                                  </p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Signup Device:</span>
                                  <p className="text-white">{user.metadata.signup.browser || 'N/A'} on {user.metadata.signup.operatingSystem || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Signup User Agent:</span>
                                  <p className="text-white text-xs break-all">{user.metadata.signup.userAgent || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Signup Screen Resolution:</span>
                                  <p className="text-white">{user.metadata.signup.screenResolution || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Signup Timezone:</span>
                                  <p className="text-white">{user.metadata.signup.timezone || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Signup Language:</span>
                                  <p className="text-white">{user.metadata.signup.language || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-400">Signup Time:</span>
                                  <p className="text-white">{formatTimestamp(user.metadata.signup.timestamp)}</p>
                                </div>
                              </>
                            ) : (
                              <p className="text-gray-500">No signup metadata available</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reject/Suspend Modal */}
      {showRejectModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xs font-semibold text-white mb-4">
              {activeTab === 'pending' ? 'Reject User' : 'Suspend User'}
            </h3>
            <p className="text-gray-300 mb-4 text-xs">
              {activeTab === 'pending' 
                ? `Are you sure you want to reject ${selectedUser.displayName}?`
                : `Are you sure you want to suspend ${selectedUser.displayName}?`
              }
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter reason..."
              className="w-full p-3 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-[#2AB7A6] text-xs"
              rows={3}
            />
            <div className="flex space-x-3 mt-4">
              <button
                onClick={() => {
                  if (activeTab === 'pending') {
                    handleReject(selectedUser.uid);
                  } else {
                    handleSuspend(selectedUser.uid);
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors text-xs"
              >
                {activeTab === 'pending' ? 'Reject' : 'Suspend'}
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setSelectedUser(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
